import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import AddMachineryDialog from '../components/AddMachineryDialog';
import HeaderMenu from '../components/HeaderMenu';
import GroupSection from '../components/GroupSection';
import EquipmentSortableCard from '../components/EquipmentSortableCard';

const UNGROUPED_ID = '__ungrouped__';

const LocationEquipment = ({ user, onLogout }) => {
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [machinery, setMachinery] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [latestLog, setLatestLog] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'chip'
  const [creatingGroupOpen, setCreatingGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const navigate = useNavigate();

  const isAdmin = user.role === 'admin' || user.role === 'technician';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    fetchData();
  }, [locationId]);

  const fetchData = async () => {
    try {
      const [locationRes, machineryRes, groupsRes, logsRes] = await Promise.all([
        axios.get(`${API}/locations/${locationId}`),
        axios.get(`${API}/locations/${locationId}/machinery`),
        axios.get(`${API}/equipment-groups?location_id=${locationId}`),
        axios.get(`${API}/locations/${locationId}/maintenance-logs?limit=1`),
      ]);
      setLocation(locationRes.data);
      setMachinery(machineryRes.data);
      setGroups(groupsRes.data);
      setLatestLog((logsRes.data && logsRes.data[0]) || null);
    } catch (error) {
      toast.error('Failed to load equipment');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filter — applied on top of group buckets.
  const filteredMachinery = useMemo(() => {
    if (!searchTerm.trim()) return machinery;
    const needle = searchTerm.toLowerCase();
    return machinery.filter((m) =>
      [m.component, m.location, m.manufacturer, m.serial_number, m.model]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [searchTerm, machinery]);

  // Group machinery: `null`/missing group_id → ungrouped; otherwise into the matching group.
  // Machinery inside a bucket is ordered by (group_sort_order asc, created_at asc, component asc).
  const buckets = useMemo(() => {
    const knownGroupIds = new Set(groups.map((g) => g.id));
    const ungrouped = [];
    const byGroup = new Map();
    groups.forEach((g) => byGroup.set(g.id, []));

    const cmp = (a, b) => {
      const ao = a.group_sort_order ?? 999999;
      const bo = b.group_sort_order ?? 999999;
      if (ao !== bo) return ao - bo;
      const ac = a.component || '';
      const bc = b.component || '';
      return ac.localeCompare(bc);
    };

    filteredMachinery.forEach((m) => {
      if (m.group_id && knownGroupIds.has(m.group_id)) {
        byGroup.get(m.group_id).push(m);
      } else {
        ungrouped.push(m);
      }
    });

    ungrouped.sort(cmp);
    byGroup.forEach((arr) => arr.sort(cmp));

    return { ungrouped, byGroup };
  }, [filteredMachinery, groups]);

  // ---- Group CRUD -----------------------------------------------------
  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/equipment-groups`, {
        plant_location_id: locationId,
        name,
      });
      setGroups((prev) => [...prev, res.data]);
      setNewGroupName('');
      setCreatingGroupOpen(false);
      toast.success(`Group "${name}" created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create group');
    }
  };

  const renameGroup = async (groupId, name) => {
    try {
      const res = await axios.put(`${API}/equipment-groups/${groupId}`, { name });
      setGroups((prev) => prev.map((g) => (g.id === groupId ? res.data : g)));
      toast.success('Group renamed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename group');
    }
  };

  // Reorder a group up or down by 1 position. Groups render in `sort_order`
  // ascending, so "up" == smaller sort_order. We optimistically reorder the
  // local state, then persist the two swapped rows' sort_orders in one
  // Promise.all so a single failure rolls both back.
  const moveGroup = async (group, direction) => {
    const idx = groups.findIndex((g) => g.id === group.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= groups.length) return;

    const prev = groups;
    const reordered = groups.slice();
    [reordered[idx], reordered[targetIdx]] = [reordered[targetIdx], reordered[idx]];
    // Rewrite sort_order so it's a contiguous 0..N-1 sequence regardless of
    // whatever gaps the DB had. Cheap and keeps the list well-formed.
    const withOrder = reordered.map((g, i) => ({ ...g, sort_order: i }));
    setGroups(withOrder);

    try {
      await axios.put(`${API}/equipment-layout`, {
        location_id: locationId,
        group_ids: withOrder.map((g) => g.id),
      });
    } catch (e) {
      setGroups(prev);  // rollback
      toast.error(e.response?.data?.detail || 'Failed to reorder groups');
    }
  };

  const moveGroupUp = (group) => moveGroup(group, 'up');
  const moveGroupDown = (group) => moveGroup(group, 'down');

  const deleteGroup = async (group) => {
    try {
      const res = await axios.delete(`${API}/equipment-groups/${group.id}`);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      // Any machinery in this group locally becomes ungrouped.
      setMachinery((prev) =>
        prev.map((m) => (m.group_id === group.id ? { ...m, group_id: null } : m))
      );
      setDeletingGroup(null);
      const n = res.data?.machinery_ungrouped ?? 0;
      toast.success(
        `Group deleted${n ? ` · ${n} item${n === 1 ? '' : 's'} moved to Ungrouped` : ''}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete group');
    }
  };

  // ---- Drag and drop --------------------------------------------------
  const groupIdFromBucketId = (bucketId) =>
    bucketId === UNGROUPED_ID ? null : bucketId;

  // Find the bucket a machinery id currently lives in.
  const findBucketOf = (machineryId) => {
    for (const g of groups) {
      const arr = buckets.byGroup.get(g.id);
      if (arr && arr.some((m) => m.id === machineryId)) return g.id;
    }
    if (buckets.ungrouped.some((m) => m.id === machineryId)) return UNGROUPED_ID;
    return null;
  };

  const onDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over) return;
    if (active.id === over.id) return;

    const overIsBucket =
      over.id === UNGROUPED_ID || groups.some((g) => `group:${g.id}` === over.id);
    // dnd-kit gives us "group:<id>" for droppable buckets and raw machinery id for sortable items.
    let targetBucketRaw;
    if (overIsBucket) {
      targetBucketRaw = over.id === 'group:__ungrouped__' ? UNGROUPED_ID : over.id.replace('group:', '');
    } else {
      // dropped onto another card — infer its bucket
      targetBucketRaw = findBucketOf(over.id);
    }
    if (targetBucketRaw == null) return;

    const sourceBucket = findBucketOf(active.id);
    if (sourceBucket == null) return;

    // Snapshot for rollback
    const prevMachinery = machinery;

    // Compute new ordering optimistically.
    let newMachinery = machinery.slice();
    const movedIdx = newMachinery.findIndex((m) => m.id === active.id);
    if (movedIdx < 0) return;

    const nextGroupId = groupIdFromBucketId(targetBucketRaw);

    // Update group_id on the moved item.
    newMachinery[movedIdx] = { ...newMachinery[movedIdx], group_id: nextGroupId };

    // Rebuild the target bucket order after the move.
    const targetBucketItems = (
      nextGroupId ? buckets.byGroup.get(nextGroupId) || [] : buckets.ungrouped
    ).slice();

    // If moving within the same bucket, reorder around `over.id`.
    if (sourceBucket === targetBucketRaw && !overIsBucket) {
      const oldIndex = targetBucketItems.findIndex((m) => m.id === active.id);
      const newIndex = targetBucketItems.findIndex((m) => m.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) {
        const reordered = arrayMove(targetBucketItems, oldIndex, newIndex);
        reordered.forEach((m, i) => {
          const j = newMachinery.findIndex((x) => x.id === m.id);
          if (j >= 0) newMachinery[j] = { ...newMachinery[j], group_sort_order: i };
        });
        newMachinery[movedIdx] = {
          ...newMachinery[movedIdx],
          group_id: nextGroupId,
        };
      }
    } else {
      // Cross-bucket move — append (or insert before over.id if dropped on a card).
      const withoutMoved = targetBucketItems.filter((m) => m.id !== active.id);
      let insertAt = withoutMoved.length;
      if (!overIsBucket) {
        const overIdx = withoutMoved.findIndex((m) => m.id === over.id);
        if (overIdx >= 0) insertAt = overIdx;
      }
      const reordered = [
        ...withoutMoved.slice(0, insertAt),
        newMachinery[movedIdx],
        ...withoutMoved.slice(insertAt),
      ];
      reordered.forEach((m, i) => {
        const j = newMachinery.findIndex((x) => x.id === m.id);
        if (j >= 0) newMachinery[j] = { ...newMachinery[j], group_sort_order: i };
      });
    }

    setMachinery(newMachinery);

    // Persist: only the moved item strictly needs a PATCH, but we also send
    // sort_order updates for its bucket peers so within-bucket order sticks.
    try {
      const bucket = nextGroupId ? buckets.byGroup.get(nextGroupId) : buckets.ungrouped;
      const persistIds = new Set(
        (bucket || []).map((m) => m.id).concat([active.id])
      );
      // If cross-bucket, also persist re-sort of source bucket in case order shifted
      // (usually it doesn't because we just removed a card, but be safe).
      if (sourceBucket !== targetBucketRaw) {
        const src = sourceBucket === UNGROUPED_ID ? buckets.ungrouped : buckets.byGroup.get(sourceBucket);
        (src || []).forEach((m) => persistIds.add(m.id));
      }
      const items = Array.from(persistIds)
        .map((mid) => newMachinery.find((x) => x.id === mid))
        .filter(Boolean)
        .map((m) => ({ id: m.id, group_id: m.group_id || null, group_sort_order: m.group_sort_order ?? null }));
      await axios.put(`${API}/equipment-layout`, { location_id: locationId, machinery: items });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save move');
      setMachinery(prevMachinery); // rollback
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  const activeMachinery = activeDragId ? machinery.find((m) => m.id === activeDragId) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <Button variant="outline" onClick={() => navigate('/')} className="mb-2">
                ← Back to Plants
              </Button>
              <h1 className="text-2xl font-bold text-slate-900">{location?.name}</h1>
              <p className="text-sm text-slate-600 mt-1">{machinery.length} equipment items</p>
            </div>
            <div className="flex gap-2">
              <HeaderMenu user={user} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {(latestLog || true) && (
          <div className="bg-white rounded-lg shadow p-4 mb-6 border-l-4 border-slate-900">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                  Most recent maintenance log
                </div>
                {latestLog ? (
                  <>
                    <div className="font-semibold text-slate-900">
                      {latestLog.equipment_name}
                      <span className="text-slate-500 font-normal text-sm ml-2">
                        {new Date(latestLog.work_date || latestLog.date).toLocaleString(undefined, {
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 mt-1 line-clamp-2 whitespace-pre-wrap">
                      {latestLog.work_description || '(no description)'}
                    </div>
                    {latestLog.resolution && (
                      <div className="text-sm text-green-800 mt-1 line-clamp-1">
                        <span className="font-medium">Resolution:</span> {latestLog.resolution}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-slate-600">No maintenance logs yet for this location.</div>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(`/location/${locationId}/maintenance-logs`)}
                data-testid="view-all-logs-btn"
              >
                View All Logs →
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/location/${locationId}/maintenance-requests`)}
                data-testid="view-requests-btn"
              >
                Maintenance Requests →
              </Button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search equipment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="equipment-search-input"
              />
            </div>
            {/* View mode toggle */}
            <div className="inline-flex rounded-md border bg-white p-1 shadow-sm self-start">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                data-testid="view-mode-card-btn"
                className={`px-3 py-1.5 text-sm rounded ${
                  viewMode === 'card' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('chip')}
                data-testid="view-mode-chip-btn"
                className={`px-3 py-1.5 text-sm rounded ${
                  viewMode === 'chip' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                Compact
              </button>
            </div>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setCreatingGroupOpen((v) => !v)}
                  data-testid="new-group-btn"
                >
                  + New Group
                </Button>
                <Button onClick={() => setShowAddDialog(true)} data-testid="add-equipment-btn">
                  Add Equipment
                </Button>
              </>
            )}
          </div>

          {creatingGroupOpen && isAdmin && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <Input
                autoFocus
                placeholder="Group name (e.g. Compressors, Actuators, Valves)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') createGroup();
                  if (e.key === 'Escape') { setCreatingGroupOpen(false); setNewGroupName(''); }
                }}
                className="max-w-sm"
                data-testid="new-group-name-input"
              />
              <Button onClick={createGroup} disabled={!newGroupName.trim()} data-testid="new-group-create-btn">
                Create
              </Button>
              <Button
                variant="outline"
                onClick={() => { setCreatingGroupOpen(false); setNewGroupName(''); }}
              >
                Cancel
              </Button>
              <span className="text-xs text-slate-500">
                Groups are purely visual — they don&apos;t change equipment data.
              </span>
            </div>
          )}
        </div>

        {/* Groups + Ungrouped */}
        {machinery.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 text-lg">No equipment found</p>
            {isAdmin && (
              <Button onClick={() => setShowAddDialog(true)} className="mt-4">
                Add Your First Equipment
              </Button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <div className="space-y-4">
              {/* Ungrouped bucket always renders (drop target) */}
              <GroupSection
                group={null}
                machinery={buckets.ungrouped}
                viewMode={viewMode}
                isAdmin={isAdmin}
                onRename={renameGroup}
                onDelete={setDeletingGroup}
              />

              {groups.map((g, idx) => (
                <GroupSection
                  key={g.id}
                  group={g}
                  machinery={buckets.byGroup.get(g.id) || []}
                  viewMode={viewMode}
                  isAdmin={isAdmin}
                  onRename={renameGroup}
                  onDelete={setDeletingGroup}
                  onMoveUp={moveGroupUp}
                  onMoveDown={moveGroupDown}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < groups.length - 1}
                />
              ))}
            </div>

            <DragOverlay>
              {activeMachinery && (
                <EquipmentSortableCard
                  machinery={activeMachinery}
                  viewMode={viewMode}
                  isAdmin={isAdmin}
                  dndDisabled
                />
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {isAdmin && (
        <AddMachineryDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          plantLocationId={locationId}
          onSuccess={() => {
            setShowAddDialog(false);
            fetchData();
          }}
        />
      )}

      {/* Delete-group confirm */}
      {deletingGroup && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDeletingGroup(null)}
          data-testid="delete-group-overlay"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Delete group &quot;{deletingGroup.name}&quot;?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Equipment currently in this group will be moved to <span className="font-medium">Ungrouped</span>.
              No maintenance data is affected.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeletingGroup(null)} data-testid="cancel-delete-group-btn">
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => deleteGroup(deletingGroup)}
                data-testid="confirm-delete-group-btn"
              >
                Delete group
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationEquipment;
